import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ArrowLeft, ChevronRight, ChevronLeft, Star, Edit, Share2, Link, Smartphone, Mail, UserPlus, X, Send, Users2, CheckCircle, User, Plus, Users, Clock, MapPin, Settings, ChevronDown, ChevronUp, UserCheck, Trophy, RefreshCw, Trash2, Shuffle, Play, Award, Shield, Zap, GitBranch, Check, AlertCircle, Radio, Info, UserCircle, BarChart3, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useStripeReturn } from '../../payments/services/PaymentStripeHooks';
import PaymentResultBanner from '../../payments/components/PaymentResultBanner';
import { useTheme, AcademyThemeScope } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { tournamentAPI, tournamentParticipantAPI, tournamentScheduleAPI } from '../services/tournamentApi';
import { eventsAPI, academyAPI } from '../../services/api';
import { buildTournamentVO } from '../tournamentVO';
import { isOrganizer as isOrganizerAccess, canManageSchedule as canManageScheduleAccess } from '../access';
import { TOURNAMENT_STATUS } from '../constants';
import { apiCall } from '../../services/apiUtil';
import { usePageRefresh } from '../../contexts/RefreshContext';
import { MatchHandlers } from '../utils/matchHandlers';
import ConfirmModal from '../../components/ConfirmModal';
import LiveGameTab from '../tabs/LiveGameTab';
import TournamentHeader from '../components/TournamentHeader';
import ShareModal from '../../components/common/ShareModal';
import ImageCropModal from '../../components/common/ImageCropModal';
import DetailsTab from '../tabs/DetailsTab';
import StandingsTab from '../tabs/StandingsTab';
import RegisterTab from '../tabs/RegisterTab';
import EnrolledTab from '../tabs/EnrolledTab';
import ScheduleTab from '../tabs/ScheduleTab';
import StatsTab from '../tabs/StatsTab';
import SmartScheduleTab from '../tabs/SmartScheduleTab';
import Tabs from '../../components/common/Tabs';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
/**
 * A tournament reached through an academy wears that academy's theme.
 *
 * The theme has to be established ABOVE the screen that reads it, so this shell
 * resolves the key and the page renders inside it — the same split
 * AcademyDetailsPage and the checkout page use. Reached at /tournament/:id with
 * no academy in the path, there is no key and Kynaix's palette applies.
 */
export default function TournamentDetailPage() {
  const { slug } = useParams();
  const [themeKey, setThemeKey] = React.useState(null);

  React.useEffect(() => {
    if (!slug) return;
    let live = true;
    academyAPI.getById(slug)
      .then(a => { if (live) setThemeKey(a?.themeKey || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [slug]);

  return (
    <AcademyThemeScope themeKey={themeKey}>
      <TournamentDetailPageInner />
    </AcademyThemeScope>
  );
}

function TournamentDetailPageInner() {
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { smartSchedule: smartScheduleEnabled, tournamentMatches: tournamentMatchesEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const { id, slug: academySlug } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pull-to-refresh (mobile app has no address-bar reload) — same simple
  // full reload as the header Refresh button, just triggered by the native
  // drag-down gesture instead of hunting for a button.
  usePageRefresh(() => window.location.reload());

  // ?tab=register takes priority, then location.state, then default
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || location.state?.activeTab || 'register'
  );
  // Tabs that have been opened at least once stay mounted (just hidden via
  // CSS) instead of unmounting — switching back to an already-visited tab
  // shows its last-loaded content instantly instead of a fresh loading
  // flicker while it re-fetches. Purely presentational: each tab's own
  // fetch/render logic is untouched, it just doesn't get torn down anymore.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));

  // Tab strip, so a tab switch can keep it on screen. ScrollToTop no longer
  // yanks the page back to the hero image on a tab-only query change, which
  // leaves the reader where they were — right unless they had scrolled deep
  // into a long tab (Enrolled, Matches) and switched to a shorter one, where
  // staying put would land them below the new panel entirely.
  const tabStripRef = useRef(null);
  const pendingTabScroll = useRef(false);

  // Bring the strip back to the top after a tab switch that started with it
  // scrolled off screen. Runs post-commit rather than from the click handler:
  // measuring in the handler reads the outgoing tab's layout, and a switch
  // into a SHORTER tab also makes the browser clamp scrollY as the document
  // shrinks — a clamp that lands after any frame-count guess and leaves the
  // reader wherever it dropped them. Re-running as the panel settles (tabs
  // like Standings and Matches fetch their data on open, so height changes
  // again after first paint) is what makes this land reliably.
  useEffect(() => {
    if (!pendingTabScroll.current) return;
    pendingTabScroll.current = false;

    let cancelled = false;
    const settle = () => {
      if (cancelled) return;
      const strip = tabStripRef.current;
      if (!strip) return;
      const top = strip.getBoundingClientRect().top;
      // Instant: a smooth animation gets cancelled outright by the height
      // changes this is compensating for.
      if (top < -1) window.scrollTo({ top: top + window.scrollY - 12, behavior: 'auto' });
    };

    // Once immediately after commit, then again as late content lands.
    settle();
    const timers = [50, 150, 400].map((ms) => setTimeout(settle, ms));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [activeTab]);

  const handleTabChange = (tab) => {
    // Was the strip already off the top when they clicked? Captured here,
    // before any re-render, because that is the only moment it reflects what
    // the reader was actually looking at.
    pendingTabScroll.current = (tabStripRef.current?.getBoundingClientRect().top ?? 0) < 0;

    setActiveTab(tab);
    setVisitedTabs(prev => prev.has(tab) ? prev : new Set(prev).add(tab));
    setSearchParams({ tab }, { replace: true, state: location.state });
  };
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [tournamentLoading, setTournamentLoading] = useState(true);

  // Prefer nav state (set when navigating from squad tournament list).
  // Fall back to tournament data itself so breadcrumb survives a page refresh.
  const fromSquad = location.state?.fromSquad ||
    (tournament?.ownerType === 'group' && tournament?.ownerId
      ? { id: tournament.ownerId, name: tournament.ownerName || 'Squad' }
      : null);

  // Same idea for academy-owned tournaments. Prefer the slug from the nested
  // /academy/:slug/tournament/:id route (nicer URL) over the raw ownerId —
  // both resolve fine since academyAPI.getById accepts either.
  const fromAcademy = location.state?.fromAcademy ||
    (tournament?.ownerType === 'academy' && tournament?.ownerId
      ? { id: academySlug || tournament.ownerId, name: tournament.ownerName || 'Academy' }
      : null);

  // Tournament Value Object for access control
  const [tournamentVO, setTournamentVO] = useState(null);

  // Tournament image URL
  const [tournamentImageUrl, setTournamentImageUrl] = useState(null);

  // Ref for accessing TournamentForm methods (image upload)
  const tournamentFormRef = useRef(null);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Image crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);

  // Coming back from Stripe. Refreshing participants is what turns the
  // "Unpaid" row into "Paid" without a manual reload.
  const {
    result: paymentResult,
    verifying: verifyingPayment,
    dismiss: dismissPaymentResult,
  } = useStripeReturn({
    // participantCount is derived from the participants array, so reloading
    // that is enough — there is no separate count to refresh here.
    onPaid: () => loadParticipants(participantsPage),
  });

  // Participants state (shared with EnrolledTab)
  const participantsRequestSeq = useRef(0);
  const [participants, setParticipants] = useState([]);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantsPagination, setParticipantsPagination] = useState(null);
  const [participantsSummary, setParticipantsSummary] = useState({ totalParticipants: 0, totalPlayers: 0, totalTeams: 0 });

  // Participant type conflict modal state
  const [participantTypeConflict, setParticipantTypeConflict] = useState({ isOpen: false, message: '' });

  // Delete tournament state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Dynamic game tabs state for live games (managed at page level for tab navigation)
  const [liveGameTabs, setLiveGameTabs] = useState([]); // Array of { gameId, label, sportName, teamsData }

  // Update active tab when location state changes (only if no ?tab= in URL)
  useEffect(() => {
    if (location.state?.activeTab && !searchParams.get('tab')) {
      handleTabChange(location.state.activeTab);
    }
  }, [location.state]);

  // Re-sync when `?tab=` changes without a remount — e.g. navigating here from the
  // Tourneys quick-nav panel while already on this tournament's page (only the query
  // string changes, so the useState initializer above never re-runs on its own).
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
      setVisitedTabs(prev => prev.has(urlTab) ? prev : new Set(prev).add(urlTab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle roundId parameter to auto-open live game
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const roundId = searchParams.get('roundId');

    if (roundId && tournament?.id) {
      // Fetch the game data and open live tab
      const openLiveGame = async () => {
        try {
          const game = await tournamentScheduleAPI.getGame(parseInt(roundId));
          if (!game) {
            toast.error('Game not found');
            return;
          }

          // Build the game tab object
          const gameTab = {
            gameId: game.id,
            label: `${game.participant_a_name || 'TBD'} vs ${game.participant_b_name || 'TBD'}`,
            sportName: game.sport_name,
            teamsData: {
              teamA: {
                id: game.participant_a_id,
                name: game.participant_a_name || 'TBD',
                type: game.participant_a_type
              },
              teamB: {
                id: game.participant_b_id,
                name: game.participant_b_name || 'TBD',
                type: game.participant_b_type
              }
            },
            event: game.event,
            tournamentGame: game,
            originTab: 'schedule'
          };

          // Open the live tab
          handleOpenLiveTab(gameTab);

          // Switch to schedule tab first, then to the game tab
          handleTabChange('schedule');
          setTimeout(() => {
            handleTabChange(`game-${game.id}`);
          }, 100);
        } catch (error) {
          console.error('Failed to load game for roundId:', error);
          toast.error('Failed to open live game');
        }
      };

      openLiveGame();
    }
  }, [location.search, tournament?.id]);

  // Load tournament data from API
  useEffect(() => {
    const loadTournament = async () => {
      try {
        setTournamentLoading(true);

        // Always try to fetch from API if we have an ID, regardless of location state
        if (id) {
          const tournamentData = await tournamentAPI.getById(id);
          setTournament(tournamentData);
        } else {
          // Try location state (if navigated from tournament list without ID)
          const tournamentFromState = location.state?.tournament;
          if (tournamentFromState) {
            setTournament(tournamentFromState);
          } else {
            console.error(" Tournament is not found through id and state both. where id="+id)
          }
        }
      } catch (error) {
        console.error('Failed to load tournament:', error);

      } finally {
        setTournamentLoading(false);
      }
    };

    loadTournament();
    // location.state intentionally excluded — handleTabChange calls
    // setSearchParams(..., { state: location.state }) on every tab switch,
    // which hands back a new location object each time (even though the
    // state content is unchanged), so including it here refetched the whole
    // tournament — and the full-page loading gate below with it — on every
    // tab click. `id` alone is what should ever trigger a real refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load tournament participants from API with pagination
  const loadParticipants = async (page = 1) => {
    if (!tournament?.id) return;

    // Two loads can be in flight at once — the mount-time one and a reload
    // triggered by something finishing, like a Stripe payment settling. Without
    // this guard the slower, older response can land last and paint stale rows
    // over fresh ones, which showed a just-paid registration as "Unpaid".
    const seq = ++participantsRequestSeq.current;
    const isStale = () => seq !== participantsRequestSeq.current;

    try {
      const participantsData = await tournamentParticipantAPI.getByTournamentId(tournament.id, page, 10);
      if (isStale()) return;

      // Handle different API response formats
      const participants = participantsData?.data || [];
      const pagination = participantsData?.pagination || null;
      const summary = participantsData?.summary || { totalParticipants: 0, totalPlayers: 0, totalTeams: 0 };


      setParticipants(participants);
      setParticipantsPagination(pagination);
      setParticipantsSummary(summary);
    } catch (error) {
      if (isStale()) return;
      console.error('Failed to load participants:', error);
      setParticipants([]);
      setParticipantsPagination(null);
      setParticipantsSummary({ totalParticipants: 0, totalPlayers: 0, totalTeams: 0 });
    }
  };

  useEffect(() => {
    loadParticipants(participantsPage);
  }, [tournament?.id, participantsPage]);

  // tournament_image now stores the CloudFront path (/images/...) — use directly
  useEffect(() => {
    setTournamentImageUrl(resolveImageUrl(tournament?.tournamentImage) || null);
  }, [tournament?.tournamentImage]);

  // Build Tournament VO whenever tournament data changes
  useEffect(() => {
    if (tournament) {
      const vo = buildTournamentVO(
        tournament,
        [], // apiRounds now managed by ScheduleTab
        participants,
        user,
        {}
      );
      setTournamentVO(vo);
    }
  }, [tournament, participants, user]);

  // Check if current user is the organizer - using access control function
  const isOrganizer = tournamentVO ? isOrganizerAccess(user?.id, tournamentVO) : false;

  // Check if user has schedule management permissions (owner or co-owner)
  // Using access control function which checks tournamentVO for proper permission logic
  const canManageSchedule = tournamentVO ? canManageScheduleAccess(user?.id, tournamentVO) : false;

  // Handler for starting edit mode
  const handleEditClick = () => {
    // Access control is handled by TournamentHeader using access.js
    setIsEditing(true);
    // Ensure the details tab is active so TournamentForm (and its file input) is mounted.
    // The camera icon in TournamentHeader routes through tournamentFormRef which only
    // exists when DetailsTab is rendered.
    handleTabChange('details');
  };

  // Handler for canceling edit
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Handler called after image is successfully uploaded to S3
  // Updates the banner image immediately without waiting for form save
  const handleImageUploaded = (key, localDataUrl) => {
    // Show the local data URL immediately for instant feedback
    setTournamentImageUrl(localDataUrl);
    // Also update tournament state so the useEffect re-fetches the presigned URL
    // when the user saves the form (which calls setTournament)
    setTournament(prev => prev ? { ...prev, tournamentImage: key } : prev);
  };

  // Handler called when user selects an image — opens crop modal before uploading
  const handleImageSelected = (dataUrl) => {
    setSelectedImageForCrop(dataUrl);
    setCropModalOpen(true);
  };

  // Handler called after crop is approved — delegates move + save to TournamentForm
  const handleCroppedImage = ({ url }) => {
    setCropModalOpen(false);
    setSelectedImageForCrop(null);
    if (tournamentFormRef.current) {
      tournamentFormRef.current.applyApprovedImage({ url });
    }
  };

  // Handler for image upload click (triggers file input in TournamentForm)
  const handleImageUploadClick = () => {
    if (!tournamentFormRef.current) {
      console.warn('[TournamentDetailPage] tournamentFormRef is null — DetailsTab must be active for image upload');
    }
    tournamentFormRef.current?.triggerImageUpload();
  };

  // Handler for share button click
  const handleShareClick = () => {
    setShareModalOpen(true);
  };

  // Handler for delete tournament click
  const handleDeleteClick = () => {
    setDeleteConfirm({ isOpen: true });
  };

  // Handler for confirming tournament deletion
  const handleConfirmDelete = async () => {
    if (!tournament) return;

    try {
      setDeleteLoading(true);
      await tournamentAPI.delete(tournament.id);
      toast.success('Tournament deleted successfully');
      setDeleteConfirm({ isOpen: false });
      if (fromAcademy) {
        navigate(`/academy/${fromAcademy.id}?tab=tournaments`);
      } else if (fromSquad) {
        navigate(`/squads/${fromSquad.id}/tournaments`);
      } else {
        navigate('/tournaments');
      }
    } catch (error) {
      console.error('Failed to delete tournament:', error);
      toast.error(error.message || 'Failed to delete tournament');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handler for opening a live game tab (receives gameTab from ScheduleTab,
  // SmartScheduleTab, or this page's own roundId-URL-param effect)
  const handleOpenLiveTab = (gameTab) => {
    // Normalize gameId to a string before anything else touches it. The
    // three callers disagree on type: ScheduleTab.jsx and this page's own
    // roundId effect both pass `game.id` straight from a raw API response
    // (a number), while useScheduleBuilder.js's startScoring/openLiveGame
    // pass `row.liveData.id`, which scheduleApi.js's buildLiveData already
    // stringifies. `t.gameId === gameTab.gameId` below is a strict-equality
    // dedup check, so 813 (number) and "813" (string) for the SAME game
    // silently pass it as "different" games and both get pushed — the two
    // resulting tabs then collide on render, since `key={gameTab.gameId}`
    // (bare) and `key={\`game-${gameTab.gameId}\`}` (template-literal, always
    // string) both stringify to the same key either way. Confirmed via the
    // "two children with the same key, `813`"/`game-813`" console warning
    // this was producing.
    // Remember which tab this game was opened from — 'schedule' (old Matches
    // tab) and 'smart_schedule' both call this, and closing/cancelling out of
    // the live game needs to land back on whichever one the user actually
    // came from, not always hardcode 'schedule'. Callers with a specific
    // origin in mind (the roundId-URL-param effect below) can override it
    // explicitly; everyone else just gets whatever tab was active at click time.
    const normalizedGameTab = { ...gameTab, gameId: String(gameTab.gameId), originTab: gameTab.originTab || activeTab };

    // Dedup check MUST happen inside the functional updater, not against the
    // `liveGameTabs` closure variable read at call time — this function can
    // fire twice in the same tick (React StrictMode deliberately double-
    // invokes effects in dev, and this is called from a useEffect reacting
    // to a roundId URL param). Two calls both reading the same stale "not
    // present yet" snapshot would both push, landing two identical tabs for
    // the same game (confirmed bug: same label rendered twice, each with its
    // own GameComponent/MatchSettingsComponent instance — the source of the
    // "two children with the same key" warning). A functional update always
    // sees the latest state regardless of how many times this runs first.
    setLiveGameTabs(prev =>
      prev.some(t => t.gameId === normalizedGameTab.gameId) ? prev : [...prev, normalizedGameTab]
    );

    // Switch to the new tab — Start Scoring/Live is usually clicked from
    // deep within a long schedule table, so land on the top of the new
    // tab's content rather than leaving the scroll position wherever it was.
    handleTabChange(`game-${normalizedGameTab.gameId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handler for closing a live game tab
  const handleCloseLiveTab = (gameId) => {
    // Look up before filtering it out, so we know where this game was opened
    // from (see the originTab comment in handleOpenLiveTab above).
    const closingTab = liveGameTabs.find(t => t.gameId === gameId);
    setLiveGameTabs(prev => prev.filter(t => t.gameId !== gameId));

    // If we're currently on this tab, switch back to wherever we came from
    if (activeTab === `game-${gameId}`) {
      handleTabChange(closingTab?.originTab || 'schedule');
    }
  };

  // Handler for game completion (completes game, event, and closes tab)
  const handleGameComplete = async (gameId, result) => {

    try {
      // Use MatchHandlers to complete the game
      const matchHandlers = new MatchHandlers({ user });
      await matchHandlers.completeGame(gameId, result, tournament);

      // Get the game details to find the associated event
      const gameDetails = await tournamentScheduleAPI.getGame(gameId);

      // Complete the associated event if it exists
      if (gameDetails?.event_id) {
        await eventsAPI.updateStatus(gameDetails.event_id, 'completed');
      }

      // Refresh tournament to show updated status
      const updatedTournament = await tournamentAPI.getById(tournament.id);
      setTournament(updatedTournament);

      // Close the live tab after completion
      handleCloseLiveTab(gameId);

      toast.success('Game and event completed!');

      // ScheduleTab refetches whenever activeTab switches back to 'schedule'
      // (handleCloseLiveTab above just did that) — not from tournament.id
      // changing, since setTournament above keeps the same .id value even
      // though it's a new object reference, so that alone wouldn't retrigger
      // an effect keyed on tournament?.id.
    } catch (error) {
      console.error('Failed to complete game:', error);
      toast.error('Failed to update game status');
    }
  };

  // Handler for saving tournament updates
  const handleSaveEdit = async (formData) => {
    if (!tournament) return;

    // Check if participantsType changed and conflicts exist
    const oldType = tournament.participantsType;
    const newType = formData.participantsType;

    if (oldType && newType && oldType !== newType) {
      const hasTeamParticipants = participantsSummary.totalTeams > 0
        || participants.some(p => p.teamId != null);
      const hasIndividualPlayers = participantsSummary.totalPlayers > 0
        || participants.some(p => p.participantId != null && p.teamId == null && p.player);

      if (newType === 'singles' && hasTeamParticipants) {
        setParticipantTypeConflict({
          isOpen: true,
          message: 'There are teams currently enrolled in this tournament. Please remove all team registrations from the Enrolled tab before changing the participant type to Singles.'
        });
        return;
      }

      if ((newType === 'doubles' || newType === 'teams') && hasIndividualPlayers) {
        const label = newType === 'doubles' ? 'Doubles' : 'Teams';
        setParticipantTypeConflict({
          isOpen: true,
          message: `There are individual players currently enrolled in this tournament. Please remove all individual player registrations from the Enrolled tab before changing the participant type to ${label}.`
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Compute dates for playNow mode
      let startDate = formData.startDate;
      let endDate = formData.endDate;
      if (formData.playNow) {
        const now = new Date();
        startDate = now.toISOString();
        endDate = new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString();
      }


      const tournamentData = {
        tournamentName: formData.tournamentName,
        description: formData.description,
        startDate,
        endDate,
        registrationDeadline: formData.registrationDeadline || null,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        registrationFee: formData.registrationFee ? parseFloat(formData.registrationFee) : null,
        participantsType: formData.participantsType,
        playersPerTeam: formData.playersPerTeam ? parseInt(formData.playersPerTeam) : null,
        sportTypeId: formData.sportTypeId ? parseInt(formData.sportTypeId) : null,
        facilityId: formData.facilityId ? parseInt(formData.facilityId) : null,
        venueName: formData.venueName || null,
        venueAddress: formData.venueAddress || null,
        venuePlaceId: formData.venuePlaceId || null,
        venueLat: formData.venueLat || null,
        venueLng: formData.venueLng || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        zipCode: formData.zipCode || null,
        streetNumber: formData.streetNumber || null,
        streetName: formData.streetName || null,
        landmark: formData.landmark || null,
        difficultyLevel: formData.difficultyLevel || null,
        ...(formData.prizeDetail && typeof formData.prizeDetail === 'object'
          ? { prizeDetail: formData.prizeDetail }
          : formData.prizeDetail && typeof formData.prizeDetail === 'string' && formData.prizeDetail.trim()
            ? { prizeDetail: { description: formData.prizeDetail } }
            : {}),
        tournamentImage: formData.tournamentImage || null,
        matchConfiguration: formData.matchConfiguration || null,
        status: formData.status || null,
        playNow: formData.playNow || false,
        requireTerms: formData.requireTerms ?? false,
        termsContentPath: formData.termsContentPath || null,
        requireWaiver: formData.requireWaiver ?? false,
        waiverContentPath: formData.waiverContentPath || null,
      };


      const result = await tournamentAPI.update(tournament.id, tournamentData);


      // Update local tournament state with API response
      setTournament(prev => ({
        ...prev,
        ...result
      }));

      toast.success('Tournament updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update tournament:', error);
      toast.error('Failed to update tournament: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleTeamClick = (teamName, teamLogo) => {
    // Navigate to team detail page with team info
    navigate(`/team/${encodeURIComponent(teamName)}`, {
      state: { teamName, teamLogo, tournament: tournament.name }
    });
  };

  // Show loading state while tournament is loading
  if (tournamentLoading || !tournament) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className={`text-lg ${theme.textSecondary}`}>Loading tournament...</div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate participant count: only count individual participants with player role = true
  const participantCount = participants.filter(p => {
    // If it's a team participant, count it
    if (p.participantType === 'team' || p.participant_type === 'team' || p.teamId || p.team_id) {
      return true;
    }
    // If it's an individual participant, only count if player role is true
    return p.player === true;
  }).length;

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Stripe sends people back to whichever tab suits the outcome, so the
            result is reported here at page level rather than inside a tab —
            tabs only mount once visited, and the Register tab is not the one
            you land on after paying. */}
        <PaymentResultBanner
          result={paymentResult}
          verifying={verifyingPayment}
          onDismiss={dismissPaymentResult}
          className="mb-5"
        />

        {/* Tournament Header */}
        <TournamentHeader
          tournament={tournament}
          tournamentVO={tournamentVO}
          participantCount={participantCount}
          tournamentImageUrl={tournamentImageUrl}
          onShare={handleShareClick}
          isEditing={isEditing}
          onImageUploadClick={handleImageUploadClick}
          isOrganizer={isOrganizer}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          fromSquad={fromSquad}
          fromAcademy={fromAcademy}
        />

        {/* Tab Navigation */}
        <div ref={tabStripRef} className={`${theme.card} rounded-xl ${theme.shadow} mb-8`}>
          {/* Tab buttons container */}
          <div className="p-4">
            <Tabs
              tabs={[
                { id: 'details', label: 'Tournament Info', shortLabel: 'Info', icon: Info },
                ...(isAuthenticated ? [{ id: 'register', label: 'Register', icon: UserPlus }] : []),
                { id: 'enrolled', label: 'Enrolled', icon: UserCircle },
                // TODO(matches-tab-removal): delete this tab and everything
                // behind it. The Matches tab was superseded by the Schedule tab
                // once smart scheduling stabilised — Schedule is now the only
                // place a tournament is scheduled, and it renders the match
                // list afterwards. This entry survives only because the
                // `tournamentMatches` flag was left switched on in a local
                // exsports_core_api/.env (the tracked .env.example has it
                // false), which brought the tab back on a machine where it was
                // believed gone.
                //
                // To remove: this line, the `tournamentMatches` flag in
                // FeatureFlagsContext.jsx, FEATURE_TOURNAMENT_MATCHES in
                // exsports_core_api (features.js + .env.example), and the
                // `'schedule'` tab body plus any component it alone renders.
                // Leaving the flag in place means the tab can reappear from a
                // config change, which is exactly what happened here.
                ...(tournamentMatchesEnabled ? [{ id: 'schedule', label: 'Matches', icon: Calendar }] : []),
                ...(smartScheduleEnabled ? [{ id: 'smart_schedule', label: 'Schedule', icon: Wand2 }] : []),
                { id: 'standings', label: 'Standings', icon: Award },
                { id: 'stats', label: 'Statistics', shortLabel: 'Stats', icon: BarChart3 },
                // Dynamic game tabs for live games
                ...liveGameTabs.map(gameTab => ({
                  id: `game-${gameTab.gameId}`,
                  label: gameTab.label,
                  icon: Radio,
                  isLive: true,
                  onClose: () => handleCloseLiveTab(gameTab.gameId)
                }))
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              // 'smart_schedule' (labeled "Schedule", same as web) takes the mobile
              // quick-access slot the old 'schedule' tab (labeled "Matches") used to
              // occupy — old tab is still reachable via the Menu dropdown for now,
              // until it's removed altogether.
              mobileVisibleTabs={['details', 'smart_schedule', 'standings', 'stats']}
            />
          </div>
          {/* End tab buttons container */}

          {/* Tab content */}
          <div className="p-4 sm:p-6">
            {/* Schedule Tab */}
            {visitedTabs.has('schedule') && (
              <div style={activeTab === 'schedule' ? undefined : { display: 'none' }}>
                <ScheduleTab
                  tournament={tournament}
                  participants={participants}
                  isOrganizer={isOrganizer}
                  activeTab={activeTab}
                  onOpenLiveTab={handleOpenLiveTab}
                  onRefreshTournament={async () => {
                    const updatedTournament = await tournamentAPI.getById(tournament.id);
                    setTournament(updatedTournament);
                    // StandingsTab will auto-reload via its useEffect
                  }}
                />
              </div>
            )}
            {/* Smart Schedule Tab (new UX, src/tournament_formats — see KYN-475). The
                tab button above is gated behind the `smartSchedule` feature flag while
                this stabilizes — content here deliberately isn't, so ?tab=smart_schedule
                still works directly for testing even with the flag off. */}
            {visitedTabs.has('smart_schedule') && (
              <div style={activeTab === 'smart_schedule' ? undefined : { display: 'none' }}>
                <SmartScheduleTab tournament={tournament} participantsSummary={participantsSummary} onOpenLiveTab={handleOpenLiveTab} activeTab={activeTab} />
              </div>
            )}

            {/* Standings Tab */}
            {visitedTabs.has('standings') && (
              <div style={activeTab === 'standings' ? undefined : { display: 'none' }}>
                <StandingsTab
                  tournament={tournament}
                  activeTab={activeTab}
                  onTeamClick={handleTeamClick}
                  canManageStandings={canManageSchedule}
                />
              </div>
            )}

            {/* Stats Tab */}
            {visitedTabs.has('stats') && (
              <div style={activeTab === 'stats' ? undefined : { display: 'none' }}>
                <StatsTab tournament={tournament} />
              </div>
            )}

            {/* Dynamic Live Game Tabs — `.find()`, not `.map()`, so this only
                ever renders ONE instance even if `liveGameTabs` somehow ends
                up holding more than one entry matching the active tab
                (confirmed: two GameComponent instances stacked on screen for
                the same `?tab=game-N` URL). handleOpenLiveTab's own dedup
                check should already prevent that at the source, but a
                browser tab left open across the fix landing (HMR preserves
                React state, so the duplicate already sitting in memory isn't
                retroactively cleaned by a code change) could still be
                carrying a stale duplicate — this is the render-side
                backstop regardless of how one gets in. */}
            {(() => {
              const activeGameTab = liveGameTabs.find(gameTab => activeTab === `game-${gameTab.gameId}`);
              if (!activeGameTab) return null;
              const gameTab = activeGameTab;
              return (
                <LiveGameTab
                  key={gameTab.gameId}
                  gameId={gameTab.gameId}
                  gameCommonId={gameTab.gameCommonId}
                  sportName={gameTab.sportName}
                  teamsData={gameTab.teamsData}
                  onClose={() => handleCloseLiveTab(gameTab.gameId)}
                  onGameComplete={handleGameComplete}
                />
              );
            })()}

            {/* Registration Tab */}
            {visitedTabs.has('register') && (
              <div style={activeTab === 'register' ? undefined : { display: 'none' }}>
                <RegisterTab
                  tournament={tournament}
                  tournamentVO={tournamentVO}
                  setActiveTab={handleTabChange}
                  loadParticipants={loadParticipants}
                />
              </div>
            )}
            {/* Enrolled Tab */}
            {visitedTabs.has('enrolled') && (
              <div style={activeTab === 'enrolled' ? undefined : { display: 'none' }}>
                <EnrolledTab
                  tournament={tournament}
                  tournamentVO={tournamentVO}
                  isOrganizer={isOrganizer}
                  participants={participants}
                  participantsPage={participantsPage}
                  participantsPagination={participantsPagination}
                  participantsSummary={participantsSummary}
                  loadParticipants={loadParticipants}
                  setParticipantsPage={setParticipantsPage}
                />
              </div>
            )}
            {/* Tournament Info Tab */}
            {visitedTabs.has('details') && (
              <div style={activeTab === 'details' ? undefined : { display: 'none' }}>
              <DetailsTab
                ref={tournamentFormRef}
                tournament={tournament}
                isOrganizer={isOrganizer}
                isEditing={isEditing}
                loading={loading}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                onImageUploaded={handleImageUploaded}
                onImageSelected={handleImageSelected}
              />
              </div>
            )}
          </div>
        </div>

        {/* Image Crop Modal */}
        <ImageCropModal
          isOpen={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedImageForCrop(null);
          }}
          imageSrc={selectedImageForCrop}
          fileType="tournament_assets"
          onCropComplete={handleCroppedImage}
          shape="rect"
          aspectRatio={16 / 9}
        />

        {/* Share Modal */}
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          entity={{
            id: tournament?.id,
            name: tournament?.tournamentName,
            description: tournament?.description
          }}
          entityType="tournament"
          urlPattern="/tournament/{id}"
          currentUser={user}
        />

        {/* Participant Type Conflict Modal */}
        <ConfirmModal
          isOpen={participantTypeConflict.isOpen}
          onClose={() => setParticipantTypeConflict({ isOpen: false, message: '' })}
          onConfirm={() => setParticipantTypeConflict({ isOpen: false, message: '' })}
          title="Cannot Change Participant Type"
          message={participantTypeConflict.message}
          confirmText="OK"
          confirmStyle="primary"
        />

        {/* Delete Tournament Confirmation Modal */}
        <ConfirmModal
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false })}
          onConfirm={handleConfirmDelete}
          title="Delete Tournament"
          message="This will permanently remove the tournament, its schedule, standings, and all enrolled participants/teams. This action cannot be undone. Played games and player stats are preserved for rating/ranking purposes, but will no longer be linked to this tournament."
          confirmText="Delete"
          confirmStyle="danger"
          loading={deleteLoading}
        />
      </div>
    </div>
  );
}